"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence, LayoutGroup, useReducedMotion } from "framer-motion";
import { mediaUrl } from "@/lib/utils";

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  imageUrl: string;
  linkUrl?: string;
  buttonText?: string;
  focalX?: number;
  focalY?: number;
}

interface DealGridHeroProps {
  slides: Slide[];
  /** rotasyon aralığı (ms) */
  intervalMs?: number;
}

const VISIBLE = 3; // 1 büyük + 2 tile
// Konveyör hareketi: spring ile gerçek kayma (fade değil).
const LAYOUT_SPRING = { type: "spring" as const, stiffness: 240, damping: 30 };

// Marketplace hero: ana banner (sol, geniş) + yan promo tile'lar (sağ).
// Slide'lar konveyör gibi akar: yeni → büyük, büyük → üst tile, üst → alt tile, alt çıkar.
// (3 slide varsa üçü yerinde döner; 3'ten fazlaysa sıradaki girer.)
export function DealGridHero({ slides, intervalMs = 5000 }: DealGridHeroProps) {
  const reduceMotion = useReducedMotion();
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);

  const n = slides.length;
  const animate = !reduceMotion && n > 1;

  useEffect(() => {
    if (!animate || paused) return;
    // start-- → yeni slide büyük slota girer, diğerleri bir slot aşağı kayar.
    const t = setInterval(() => setStart((s) => (s - 1 + n) % n), intervalMs);
    return () => clearInterval(t);
  }, [animate, paused, n, intervalMs]);

  if (n === 0) return null;

  // Görünür slotları (start'a göre) hesapla. slot 0 = büyük, 1 = üst, 2 = alt.
  const visible = Array.from({ length: Math.min(VISIBLE, n) }, (_, slot) => ({
    slot,
    slide: slides[(start + slot) % n],
  }));

  return (
    <div
      className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:grid-rows-2 lg:h-[34rem]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <LayoutGroup>
        <AnimatePresence mode="popLayout" initial={false}>
          {visible.map(({ slot, slide }) => (
            <SlideCard
              key={slide.id}
              slide={slide}
              isMain={slot === 0}
              animate={animate}
            />
          ))}
        </AnimatePresence>
      </LayoutGroup>
    </div>
  );
}

function SlideCard({
  slide,
  isMain,
  animate,
}: {
  slide: Slide;
  isMain: boolean;
  animate: boolean;
}) {
  const slotClass = isMain
    ? "aspect-[16/10] lg:aspect-auto lg:col-span-2 lg:row-span-2 lg:h-full"
    : "aspect-[4/3] lg:aspect-auto lg:h-full";

  const motionProps = animate
    ? {
        layout: true as const,
        initial: { opacity: 0, scale: 0.92 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.92 },
        transition: { layout: LAYOUT_SPRING, opacity: { duration: 0.4 }, scale: { duration: 0.4 } },
      }
    : {};

  return (
    <motion.div {...motionProps} className={`group relative overflow-hidden rounded-[var(--sf-radius)] ${slotClass}`}>
      <Link href={slide.linkUrl || "/products"} className="absolute inset-0">
        <Image
          src={mediaUrl(slide.imageUrl)}
          alt={slide.title}
          fill
          priority={isMain}
          sizes={isMain ? "(max-width: 1024px) 100vw, 66vw" : "(max-width: 1024px) 50vw, 33vw"}
          style={{ objectPosition: `${slide.focalX ?? 50}% ${slide.focalY ?? 50}%` }}
          className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        {isMain ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 to-transparent" />
            <motion.div layout="position" className="absolute inset-0 flex flex-col justify-center p-6 sm:p-10">
              {slide.subtitle && (
                <span className="mb-2 w-fit rounded-[var(--sf-radius)] bg-[var(--sf-accent)] px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[var(--sf-hero-btn-text)]">
                  {slide.subtitle}
                </span>
              )}
              <h1 className="max-w-lg text-2xl font-bold leading-tight text-white sm:text-4xl">
                {slide.title}
              </h1>
              {slide.linkUrl && (
                <span className="mt-4 inline-flex w-fit items-center rounded-[var(--sf-radius)] bg-[var(--sf-hero-btn-bg)] px-5 py-2.5 text-sm font-semibold text-[var(--sf-hero-btn-text)]">
                  {slide.buttonText || "Alışverişe Başla"}
                </span>
              )}
            </motion.div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-black/30" />
            <motion.div layout="position" className="absolute inset-x-0 bottom-0 p-3">
              <p className="text-sm font-bold text-white drop-shadow">{slide.title}</p>
            </motion.div>
          </>
        )}
      </Link>
    </motion.div>
  );
}
